/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { FC } from 'react';
import { Campaign, Lookup, CampaignActivity } from '../../api/types/types';
import TestIds from '../../../../testIds';
import CampaignFormContent from '../CompleteActivityModal/CampaignFormContent';
import { useCampaignForm } from '../CompleteActivityModal/useCampaignForm';
import { CampaignFormData } from '../CompleteActivityModal/types';
import { transformFormDataToCampaignResponses } from '../CompleteActivityModal/transformUtils';
import { getLookupByName } from '../../utils/lookupUtils';
import ActivityModalActionBar from '../ActivityModalActionBar/ActivityModalActionBar';

export interface CreateOrUpdateActivityModalStep2Props {
  campaigns: Campaign[];
  lookups?: Lookup[];
  activityName?: string;
  onBack: () => void;
  onStep2Complete?: (campaignResponses: CampaignActivity[]) => Promise<void>;
  onSubmitFallback: () => void;
  open: boolean;
  showLoadingIndicator: boolean;
  loadingIndicatorText?: string;
}

const CreateOrUpdateActivityModalStep2: FC<CreateOrUpdateActivityModalStep2Props> = ({
  campaigns,
  lookups,
  activityName,
  onBack,
  onStep2Complete,
  onSubmitFallback,
  open,
  showLoadingIndicator,
}): JSX.Element => {
  const responseCodeLookup = getLookupByName(lookups ?? [], 'CampaignResponseCodes');
  const reasonLookup = getLookupByName(lookups ?? [], 'CampaignResponseReasons');
  const typeLookup = getLookupByName(lookups ?? [], 'CampaignResponseTypes');
  const competitorLookup = getLookupByName(lookups ?? [], 'Competitors');

  const campaignForm = useCampaignForm({
    campaigns,
    reasonLookup,
    typeLookup,
    competitorLookup,
    resetOnOpen: true,
    open,
  });

  const {
    methods: campaignFormMethods,
    validateForm: validateCampaignForm,
  } = campaignForm;

  const {
    formState: { isSubmitting: isCampaignSubmitting },
    trigger: triggerCampaignForm,
    handleSubmit: handleCampaignSubmit,
  } = campaignFormMethods;

  const handleStep2Submit = async (formData: Record<string, CampaignFormData>) => {
    await triggerCampaignForm();
    const isValid = await validateCampaignForm();
    if (!isValid) {
      return;
    }

    const campaignResponses = transformFormDataToCampaignResponses(
      campaigns,
      formData,
      competitorLookup,
    );

    if (onStep2Complete) {
      await onStep2Complete(campaignResponses);
    } else {
      onSubmitFallback();
    }
  };

  return (
    <form onSubmit={handleCampaignSubmit(handleStep2Submit)}>
      <CampaignFormContent
        campaigns={campaigns}
        responseCodeLookup={responseCodeLookup}
        competitorLookup={competitorLookup}
        activityName={activityName}
        campaignForm={campaignForm}
      />
      <ActivityModalActionBar
        showLoadingIndicator={showLoadingIndicator || isCampaignSubmitting}
        loadingIndicatorText="Completing activity"
        loadingIndicatorTestId={TestIds.feat.activities.completeActivityModal.loading}
        closeButtonText='Back'
        closeButtonTestId={TestIds.feat.activities.completeActivityModal.cancelButton}
        submitButtonText='Complete Activity'
        submitButtonTestId={TestIds.feat.activities.completeActivityModal.completeButton}
        onClose={onBack}
        onSubmit={handleCampaignSubmit(handleStep2Submit)}
      />
    </form>
  );
};

export default CreateOrUpdateActivityModalStep2;

